import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Close, Groups, InfoOutlined, MoreVert, OpenInNew, Remove, Search as SearchIcon } from '@mui/icons-material';
import {
  apiAddComment,
  apiListClubMembers,
  apiCreateClubPost,
  apiDeleteComment,
  apiDeletePost,
  apiGetClubs,
  apiJoinClub,
  apiLeaveClub,
  apiListMyClubs,
  apiListClubPosts,
  type ClubMember,
  type ClubListItemDto,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import { Club } from '../../types';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

type ClubPost = {
  id: string;
  content: string;
  createdAt: Date;
  author: {
    id: string;
    displayName?: string | null;
    profileImage?: string | null;
  };
  comments: ClubComment[];
};

type ClubComment = {
  id: string;
  content: string | null;
  parentId?: string | null;
  createdAt: Date;
  author: {
    id: string;
    displayName?: string | null;
    profileImage?: string | null;
  };
  children: ClubComment[];
};

function shapeClub(dto: ClubListItemDto): Club {
  return {
    id: dto.id,
    code: dto.code,
    slug: dto.slug,
    name: dto.name,
    category: dto.category ?? undefined,
    shortDesc: dto.shortDesc ?? undefined,
    description: dto.description ?? undefined,
    imageUrl: dto.imageUrl ?? undefined,
    profileUrl: dto.profileUrl ?? undefined,
    url: dto.url ?? undefined,
    memberCount: dto.memberCount ?? 0,
    joined: !!dto.joined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

function shapePost(raw: any): ClubPost {
  const shapeComment = (value: any): ClubComment => ({
    id: String(value?.id ?? ''),
    content: value?.content == null ? null : String(value.content),
    parentId: value?.parentId ?? null,
    createdAt: new Date(value?.createdAt),
    author: {
      id: String(value?.author?.id ?? ''),
      displayName: value?.author?.displayName ?? 'Unknown Student',
      profileImage: value?.author?.profileImage ?? null,
    },
    children: (value?.children || []).map(shapeComment),
  });

  return {
    id: String(raw?.id ?? ''),
    content: String(raw?.content ?? ''),
    createdAt: new Date(raw?.createdAt),
    author: {
      id: String(raw?.author?.id ?? ''),
      displayName: raw?.author?.displayName ?? 'Unknown Student',
      profileImage: raw?.author?.profileImage ?? null,
    },
    comments: (raw?.comments || []).map(shapeComment),
  };
}

function normalizeClubValue(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

const Clubs: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: me } = useAuth();
  const isSuperAdmin = String(me?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const theme = useTheme();
  const isMobileDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [myClubs, setMyClubs] = useState<Club[]>([]);
  // Start true: these gate the empty states, and starting false flashed
  // "No clubs found" / "You haven't joined any clubs yet." before the first fetch.
  const [myClubsLoading, setMyClubsLoading] = useState(true);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const pageWindowSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [pageWindowStart, setPageWindowStart] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [clubMenuAnchorEl, setClubMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionIntent, setActionIntent] = useState<'join' | 'leave' | null>(null);
  const [chatPosts, setChatPosts] = useState<ClubPost[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatCursor, setChatCursor] = useState<string | null>(null);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<{ postId: string; parentId?: string; label: string } | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersCursor, setMembersCursor] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unseenClubIds, setUnseenClubIds] = useState<string[]>([]);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLInputElement | null>(null);
  const clubSeenMapKey = useMemo(() => (me?.id ? `dragonsframe_seen_club_channels_${me.id}` : null), [me?.id]);
  const unseenClubIdsKey = useMemo(() => (me?.id ? `dragonsframe_unseen_club_ids_${me.id}` : null), [me?.id]);
  const requestedClub = useMemo(() => searchParams.get('club') || '', [searchParams]);
  const requestedClubKey = useMemo(() => normalizeClubValue(requestedClub), [requestedClub]);
  const isClubMenuOpen = Boolean(clubMenuAnchorEl);

  const isJoinLoadingFor = (clubId?: string | null) => actionLoading === clubId && actionIntent === 'join';
  const isLeaveLoadingFor = (clubId?: string | null) => actionLoading === clubId && actionIntent === 'leave';

  const syncUnseenClubIds = () => {
    if (!unseenClubIdsKey) {
      setUnseenClubIds([]);
      return;
    }
    try {
      const raw = localStorage.getItem(unseenClubIdsKey);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      setUnseenClubIds(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
    } catch {
      setUnseenClubIds([]);
    }
  };

  const markClubSeen = (clubId: string) => {
    if (!clubSeenMapKey || !unseenClubIdsKey || !clubId) return;
    try {
      const rawSeen = localStorage.getItem(clubSeenMapKey);
      const seen = rawSeen ? (JSON.parse(rawSeen) as Record<string, unknown>) : {};
      seen[clubId] = Date.now();
      localStorage.setItem(clubSeenMapKey, JSON.stringify(seen));
    } catch {
      localStorage.setItem(clubSeenMapKey, JSON.stringify({ [clubId]: Date.now() }));
    }

    try {
      const rawUnseen = localStorage.getItem(unseenClubIdsKey);
      const parsed = rawUnseen ? (JSON.parse(rawUnseen) as unknown) : [];
      const current = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
      const next = current.filter((id) => id !== clubId);
      localStorage.setItem(unseenClubIdsKey, JSON.stringify(next));
      setUnseenClubIds(next);
    } catch {
      setUnseenClubIds((prev) => {
        const next = prev.filter((id) => id !== clubId);
        localStorage.setItem(unseenClubIdsKey, JSON.stringify(next));
        return next;
      });
    }
  };

  const isClubUnread = (clubId?: string | null) => {
    if (!clubId) return false;
    return unseenClubIds.includes(clubId);
  };

  const goToProfile = (userId?: string | null) => {
    if (!userId) return;
    setDialogOpen(false);
    setMembersOpen(false);
    setMobileInfoOpen(false);
    const target = me?.id && userId === me.id ? '/profile' : `/profile/${userId}`;
    navigate(target);
  };

  const formatChatTime = (date: Date) => {
    const diffMs = Math.max(0, Date.now() - date.getTime());
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    const now = new Date();
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
    });
  };

  const isSameDay = (a: Date, b: Date) => (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );

  const formatDaySeparator = (date: Date) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((todayStart.getTime() - dateStart.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  function appendReplyToComments(nodes: ClubComment[], parentId: string, child: ClubComment): ClubComment[] {
    return nodes.map((comment) => {
      if (comment.id === parentId) {
        return { ...comment, children: [...(comment.children || []), child] };
      }
      if (comment.children?.length) {
        return { ...comment, children: appendReplyToComments(comment.children, parentId, child) };
      }
      return comment;
    });
  }

  function removeCommentFromTree(node: ClubComment, targetId: string): ClubComment | null {
    if (node.id === targetId) return null;
    return {
      ...node,
      children: (node.children || [])
        .map((child) => removeCommentFromTree(child, targetId))
        .filter(Boolean) as ClubComment[],
    };
  }

  function softDeleteCommentInTree(node: ClubComment, targetId: string): ClubComment {
    if (node.id === targetId) {
      return {
        ...node,
        content: null,
        author: { id: '', displayName: '[deleted]', profileImage: null },
      };
    }
    return {
      ...node,
      children: (node.children || []).map((child) => softDeleteCommentInTree(child, targetId)),
    };
  }

  function applyClubUpdate(clubId: string, patch: Partial<Club>) {
    setClubs((prev) => prev.map((club) => (club.id === clubId ? { ...club, ...patch } : club)));
    setMyClubs((prev) => prev.map((club) => (club.id === clubId ? { ...club, ...patch } : club)));
    setSelectedClub((prev) => (prev?.id === clubId ? { ...prev, ...patch } : prev));
  }

  function getClubById(clubId: string) {
    return clubs.find((entry) => entry.id === clubId)
      || myClubs.find((entry) => entry.id === clubId)
      || (selectedClub?.id === clubId ? selectedClub : null);
  }

  async function loadMyClubs(options?: { silent?: boolean; signal?: AbortSignal }) {
    const silent = !!options?.silent;
    if (!silent) setMyClubsLoading(true);
    try {
      const response = await apiListMyClubs(options?.signal);
      const items = (response.items || []).map(shapeClub);
      setMyClubs(items);
    } catch (err) {
      if ((err as any)?.name === 'AbortError') return;
      console.error('Error loading my clubs:', err);
      setMyClubs([]);
    } finally {
      if (!silent && !options?.signal?.aborted) setMyClubsLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    void loadMyClubs({ signal: controller.signal });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    syncUnseenClubIds();
    if (!unseenClubIdsKey) return;

    const onFocus = () => syncUnseenClubIds();
    const intervalId = window.setInterval(syncUnseenClubIds, 15000);
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [unseenClubIdsKey]);

  useEffect(() => {
    setCurrentPage(1);
    setPageWindowStart(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!requestedClub || searchQuery) return;
    setSearchQuery(requestedClub);
  }, [requestedClub, searchQuery]);

  useEffect(() => {
    setPageWindowStart((prev) => {
      const maxStart = Math.max(1, Math.floor((Math.max(1, totalPages) - 1) / pageWindowSize) * pageWindowSize + 1);
      return Math.min(prev, maxStart);
    });
  }, [totalPages]);

  useEffect(() => {
    const controller = new AbortController();
    // Flag loading synchronously — only the fetch is debounced. Setting it inside the
    // timeout let the "No clubs found" empty state flash for the debounce window.
    setLoading(true);
    const timeoutId = setTimeout(() => {
      const run = async () => {
        setError(null);
        try {
          const response = await apiGetClubs(searchQuery, undefined, pageSize, currentPage, controller.signal);
          const items = (response.items || []).map(shapeClub);
          setClubs(items);
          setTotalPages(Math.max(1, response.totalPages ?? 1));
          setSelectedClub((prev) => {
            if (!prev) return prev;
            return items.find((club) => club.id === prev.id) || prev;
          });
        } catch (err) {
          if ((err as any)?.name === 'AbortError') return;
          setError('Failed to load clubs');
          console.error('Error loading clubs:', err);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      };
      void run();
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [searchQuery, currentPage]);

  useEffect(() => {
    if (!requestedClubKey || loading) return;

    const byName = (entry: Club) => normalizeClubValue(entry.name) === requestedClubKey;
    const bySlug = (entry: Club) => normalizeClubValue(entry.slug) === requestedClubKey;
    const byCode = (entry: Club) => normalizeClubValue(entry.code) === requestedClubKey;
    const allCandidates = [...myClubs, ...clubs];
    const match = allCandidates.find((entry) => byName(entry) || bySlug(entry) || byCode(entry));
    if (!match) return;

    handleClubClick(match);
    const next = new URLSearchParams(searchParams);
    next.delete('club');
    setSearchParams(next, { replace: true });
  }, [requestedClubKey, loading, myClubs, clubs, searchParams, setSearchParams]);

  async function loadClubChat(clubId: string, cursor?: string) {
    setChatLoading(true);
    try {
      const response: any = await apiListClubPosts(clubId, cursor, 20);
      const items = (response?.items || []).map(shapePost);
      setChatPosts((prev) => (cursor ? [...prev, ...items] : items));
      setChatCursor(response?.nextCursor ?? null);
    } catch (err) {
      console.error('Error loading club chat:', err);
      if (!cursor) setChatPosts([]);
    } finally {
      setChatLoading(false);
    }
  }

  async function loadClubMembers(clubId: string, cursor?: string) {
    setMembersLoading(true);
    try {
      const response = await apiListClubMembers(clubId, undefined, cursor, 25);
      const items = response?.items || [];
      setMembers((prev) => (cursor ? [...prev, ...items] : items));
      setMembersCursor(response?.nextCursor ?? null);
    } catch (err) {
      console.error('Error loading club members:', err);
      if (!cursor) {
        setMembers([]);
        setMembersCursor(null);
      }
    } finally {
      setMembersLoading(false);
    }
  }

  useEffect(() => {
    if (!dialogOpen || !selectedClub?.id || !selectedClub.joined) {
      setChatPosts([]);
      setChatCursor(null);
      return;
    }
    void loadClubChat(selectedClub.id);
  }, [dialogOpen, selectedClub?.id, selectedClub?.joined]);

  useEffect(() => {
    const node = chatContainerRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [chatPosts.length]);

  const orderedChatPosts = useMemo(() => [...chatPosts].reverse(), [chatPosts]);

  useEffect(() => {
    if (!dialogOpen || !replyTarget) return;
    requestAnimationFrame(() => {
      composerInputRef.current?.focus();
      const length = composerInputRef.current?.value.length ?? 0;
      composerInputRef.current?.setSelectionRange(length, length);
    });
  }, [dialogOpen, replyTarget]);

  const handleJoinClub = async (clubId: string) => {
    const club = getClubById(clubId);
    if (!club || club.joined) return;

    setActionLoading(clubId);
    setActionIntent('join');
    applyClubUpdate(clubId, { joined: true, memberCount: (club.memberCount ?? 0) + 1 });
    try {
      const response: any = await apiJoinClub(clubId);
      applyClubUpdate(clubId, {
        joined: response?.joined ?? true,
        memberCount: response?.memberCount ?? (club.memberCount ?? 0) + 1,
      });
      void loadMyClubs({ silent: true });
      if (selectedClub?.id === clubId) {
        void loadClubChat(clubId);
      }
    } catch (err) {
      console.error('Error joining club:', err);
      applyClubUpdate(clubId, { joined: false, memberCount: Math.max(0, (club.memberCount ?? 1) - 1) });
    } finally {
      setActionLoading(null);
      setActionIntent(null);
    }
  };

  const handleLeaveClub = async (clubId: string) => {
    const club = getClubById(clubId);
    if (!club || !club.joined) return;

    setActionLoading(clubId);
    setActionIntent('leave');
    applyClubUpdate(clubId, { joined: false, memberCount: Math.max(0, (club.memberCount ?? 1) - 1) });
    try {
      const response: any = await apiLeaveClub(clubId);
      applyClubUpdate(clubId, {
        joined: response?.joined ?? false,
        memberCount: response?.memberCount ?? Math.max(0, (club.memberCount ?? 1) - 1),
      });
      void loadMyClubs({ silent: true });
      setChatPosts([]);
      setChatCursor(null);
    } catch (err) {
      console.error('Error leaving club:', err);
      applyClubUpdate(clubId, { joined: true, memberCount: (club.memberCount ?? 0) + 1 });
    } finally {
      setActionLoading(null);
      setActionIntent(null);
    }
  };

  const handleClubClick = (club: Club) => {
    if (club.joined) markClubSeen(club.id);
    setSelectedClub(club);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setMobileInfoOpen(false);
    setClubMenuAnchorEl(null);
    setSelectedClub(null);
    setMembersOpen(false);
    setNewMessage('');
    setChatPosts([]);
    setChatCursor(null);
    setReplyTarget(null);
    setDeletingPostId(null);
    setDeletingCommentId(null);
    setMembers([]);
    setMembersCursor(null);
  };

  const handleOpenClubMenu = (event: React.MouseEvent<HTMLElement>) => {
    setClubMenuAnchorEl(event.currentTarget);
  };

  const handleCloseClubMenu = () => {
    setClubMenuAnchorEl(null);
  };

  const handleLeaveClubFromMenu = () => {
    if (selectedClub?.joined) {
      void handleLeaveClub(selectedClub.id);
    }
    handleCloseClubMenu();
  };

  const handleOpenMembers = async () => {
    if (!selectedClub?.id || !selectedClub.joined) return;
    setMembersOpen(true);
    setMembers([]);
    setMembersCursor(null);
    await loadClubMembers(selectedClub.id);
  };

  const handleOpenProfile = (club: Club) => {
    if (club.profileUrl) {
      window.open(club.profileUrl, '_blank');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedClub?.id || !selectedClub.joined) return;
    const content = newMessage.trim();
    if (!content) return;

    const activeReply = replyTarget;
    if (activeReply) {
      setNewMessage('');
      setReplyTarget(null);
      try {
        const saved: any = await apiAddComment(activeReply.postId, { content, parentId: activeReply.parentId });
        const shaped = {
          id: String(saved?.id ?? ''),
          content: saved?.content == null ? null : String(saved.content),
          parentId: saved?.parentId ?? null,
          createdAt: new Date(saved?.createdAt),
          author: {
            id: String(saved?.author?.id ?? ''),
            displayName: saved?.author?.displayName ?? 'Unknown Student',
            profileImage: saved?.author?.profileImage ?? null,
          },
          children: [],
        } as ClubComment;

        setChatPosts((prev) => prev.map((post) => {
          if (post.id !== activeReply.postId) return post;
          if (!activeReply.parentId) {
            return { ...post, comments: [...(post.comments || []), shaped] };
          }
          return { ...post, comments: appendReplyToComments(post.comments || [], activeReply.parentId, shaped) };
        }));
      } catch (err) {
        console.error('Error sending club reply:', err);
        setNewMessage(content);
        setReplyTarget(activeReply);
      }
      return;
    }

    setSendingMessage(true);
    try {
      const created: any = await apiCreateClubPost(selectedClub.id, content);
      setChatPosts((prev) => [shapePost(created), ...prev]);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending club message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!postId || deletingPostId) return;
    setDeletingPostId(postId);
    try {
      await apiDeletePost(postId);
      setChatPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      console.error('Error deleting club post:', err);
    } finally {
      setDeletingPostId(null);
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    if (!postId || !commentId || deletingCommentId) return;
    setDeletingCommentId(commentId);
    try {
      const resp = await apiDeleteComment(commentId);
      setChatPosts((prev) => prev.map((post) => {
        if (post.id !== postId) return post;
        const nextComments = (post.comments || [])
          .map((comment) => ((resp as any)?.softDeleted ? softDeleteCommentInTree(comment, commentId) : removeCommentFromTree(comment, commentId)))
          .filter(Boolean) as ClubComment[];
        return { ...post, comments: nextComments };
      }));
    } catch (err) {
      console.error('Error deleting club comment:', err);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const pagedClubs = clubs;
  const pageWindowEnd = Math.min(totalPages, pageWindowStart + pageWindowSize - 1);

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, letterSpacing: '-0.02em' }}>
          <Groups sx={{ color: 'primary.main' }} />
          Clubs & Organizations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Discover, join, and chat with members in your school's student organizations.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search clubs and organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, letterSpacing: '-0.01em' }}>My Clubs</Typography>
        {myClubsLoading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={`my-clubs-skeleton-${index}`}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} animation="wave" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="75%" height={24} animation="wave" />
                        <Skeleton variant="text" width="45%" height={18} animation="wave" />
                      </Box>
                    </Box>
                    <Skeleton variant="text" width="95%" height={20} animation="wave" />
                    <Skeleton variant="text" width="75%" height={20} animation="wave" />
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Skeleton variant="rounded" width={88} height={30} animation="wave" />
                    <Skeleton variant="rounded" width={78} height={30} animation="wave" />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : myClubs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You haven’t joined any clubs yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {myClubs.map((club) => (
              <Grid item xs={12} sm={6} md={4} key={`my-${club.id}`}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  {isClubUnread(club.id) && (
                    <Chip
                      size="small"
                      color="error"
                      label="New messages"
                      sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2, pointerEvents: 'none' }}
                    />
                  )}
                  <CardActionArea onClick={() => handleClubClick(club)} sx={{ flexGrow: 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar src={club.imageUrl || undefined} sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700, width: 40, height: 40 }}>
                          {club.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" noWrap>
                            {club.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(club.memberCount ?? 0).toLocaleString()} member{(club.memberCount ?? 0) === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                      </Box>
                      {club.shortDesc && (
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {club.shortDesc}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClubClick(club);
                      }}
                    >
                      View Club
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<Remove />}
                      disabled={actionLoading === club.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleLeaveClub(club.id);
                      }}
                    >
                      {isLeaveLoadingFor(club.id) ? 'Leaving...' : isJoinLoadingFor(club.id) ? 'Joining...' : 'Leave'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 800, letterSpacing: '-0.01em' }}>All Clubs</Typography>

      {loading && (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={`clubs-skeleton-${index}`}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} animation="wave" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="68%" height={28} animation="wave" />
                      <Skeleton variant="rounded" width={90} height={22} animation="wave" />
                      <Skeleton variant="text" width="48%" height={18} animation="wave" />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width="95%" height={20} animation="wave" />
                  <Skeleton variant="text" width="80%" height={20} animation="wave" />
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Skeleton variant="rounded" width={88} height={30} animation="wave" />
                  <Skeleton variant="rounded" width={88} height={30} animation="wave" />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && (
        <Grid container spacing={3}>
          {pagedClubs.map((club) => (
            <Grid item xs={12} sm={6} md={4} key={club.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {club.joined && isClubUnread(club.id) && (
                  <Chip
                    size="small"
                    color="error"
                    label="New messages"
                    sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2, pointerEvents: 'none' }}
                  />
                )}
                <CardActionArea onClick={() => handleClubClick(club)} sx={{ flexGrow: 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar src={club.imageUrl || undefined} sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700, width: 48, height: 48 }}>
                        {club.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {club.name}
                        </Typography>
                        {club.category && <Chip label={club.category} size="small" variant="outlined" sx={{ mb: 0.5 }} />}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {(club.memberCount ?? 0).toLocaleString()} member{(club.memberCount ?? 0) === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                    </Box>

                    {club.shortDesc && (
                      <Typography variant="body2" color="text.secondary">
                        {club.shortDesc}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClubClick(club);
                    }}
                  >
                    View Club
                  </Button>
                  {club.joined ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<Remove />}
                      disabled={actionLoading === club.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleLeaveClub(club.id);
                      }}
                    >
                      {isLeaveLoadingFor(club.id) ? 'Leaving...' : isJoinLoadingFor(club.id) ? 'Joining...' : 'Leave'}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add />}
                      disabled={actionLoading === club.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleJoinClub(club.id);
                      }}
                    >
                      {isJoinLoadingFor(club.id) ? 'Joining...' : isLeaveLoadingFor(club.id) ? 'Leaving...' : 'Join'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && clubs.length > 0 && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pageWindowStart === 1}
            onClick={() => setPageWindowStart((start) => Math.max(1, start - pageWindowSize))}
          >
            {'<'}
          </Button>
          {Array.from({ length: Math.max(0, pageWindowEnd - pageWindowStart + 1) }, (_, index) => pageWindowStart + index).map((page) => (
            <Button
              key={page}
              size="small"
              variant={page === currentPage ? 'contained' : 'outlined'}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            disabled={pageWindowEnd >= totalPages}
            onClick={() => setPageWindowStart((start) => Math.min(totalPages, start + pageWindowSize))}
          >
            {'>'}
          </Button>
        </Box>
      )}

      {!loading && clubs.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No clubs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Try adjusting your search terms.' : 'No clubs are available at the moment.'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '92vh', md: '92vh' },
            maxHeight: { xs: '92vh', md: '92vh' },
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ py: { xs: 1.25, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 } }}>
            <Avatar
              src={selectedClub?.imageUrl || undefined}
              sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700, width: { xs: 38, sm: 48 }, height: { xs: 38, sm: 48 } }}
            >
              {selectedClub?.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant={isMobileDialog ? 'subtitle1' : 'h6'}
                component="div"
                sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {selectedClub?.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {selectedClub?.category && <Chip label={selectedClub.category} size="small" variant="outlined" />}
                <Button
                  size="small"
                  variant="text"
                  sx={{ px: 0.5, minWidth: 'auto', display: { xs: 'none', sm: 'inline-flex' } }}
                  disabled={!selectedClub?.joined}
                  onClick={() => {
                    void handleOpenMembers();
                  }}
                >
                  {(selectedClub?.memberCount ?? 0).toLocaleString()} member{(selectedClub?.memberCount ?? 0) === 1 ? '' : 's'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isMobileDialog && (
                <IconButton
                  size="small"
                  aria-label="Club info"
                  onClick={() => setMobileInfoOpen(true)}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              )}
              {selectedClub?.joined && (
                <IconButton
                  size="small"
                  aria-label="Club actions"
                  aria-controls={isClubMenuOpen ? 'club-actions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={isClubMenuOpen ? 'true' : undefined}
                  onClick={handleOpenClubMenu}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                aria-label="Close club dialog"
                onClick={handleCloseDialog}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <Menu
          id="club-actions-menu"
          anchorEl={clubMenuAnchorEl}
          open={isClubMenuOpen}
          onClose={handleCloseClubMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleLeaveClubFromMenu}
            disabled={!selectedClub?.joined || actionLoading === selectedClub?.id}
            sx={{ color: 'error.main' }}
          >
            {isLeaveLoadingFor(selectedClub?.id) ? 'Leaving...' : isJoinLoadingFor(selectedClub?.id) ? 'Joining...' : 'Leave Channel'}
          </MenuItem>
        </Menu>

        <DialogContent dividers sx={{ p: { xs: 1.25, sm: 2 }, overflow: 'hidden' }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={6} sx={{ height: '100%', order: { xs: 2, md: 1 }, display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    flex: 1,
                    minHeight: 0,
                    maxHeight: { xs: 180, md: 'none' },
                    overflowY: 'auto',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  {selectedClub?.description ? (
                    <Box
                      sx={{
                        '& p': { m: 0, mb: 1.25 },
                        '& a': { textDecoration: 'underline' },
                        ...(isMobileDialog ? { maxHeight: 90, overflow: 'hidden' } : {}),
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedClub.description || '') }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={isMobileDialog ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : undefined}
                    >
                      {selectedClub?.shortDesc || 'No description available yet.'}
                    </Typography>
                  )}
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{ p: 1, flex: 1, minHeight: 0, overflowY: 'auto', display: { xs: 'none', md: 'block' } }}
                >
                  <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, mb: 0.5 }}>
                    Events
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
                    This club hasn't posted events here yet — campus-wide happenings live in{' '}
                    <Link component={RouterLink} to="/events" underline="hover">Campus Events</Link>.
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ height: '100%', order: { xs: 1, md: 2 }, minHeight: 0 }}>
              <Paper
                variant="outlined"
                sx={{ p: { xs: 1.25, sm: 1.5 }, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Member Group Chat
                </Typography>

                {!selectedClub?.joined ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Join this club to view members and access the group chat.
                  </Alert>
                ) : (
                  <>
                    <Box
                      ref={chatContainerRef}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 0.5,
                        mb: 1,
                      }}
                    >
                      {chatLoading && chatPosts.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : chatPosts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                          No messages yet. Start the conversation.
                        </Typography>
                      ) : (
                        <Box>
                          {orderedChatPosts.map((post, idx) => {
                            const prev = orderedChatPosts[idx - 1];
                            const showIdentity = !prev || prev.author?.id !== post.author?.id;
                            return (
                            <React.Fragment key={post.id}>
                            <Box sx={{ mb: showIdentity ? 2.5 : 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                {showIdentity ? (
                                  <Avatar
                                    src={post.author.profileImage || undefined}
                                    sx={{ width: 34, height: 34, background: AVATAR_GRAD, color: '#fff', fontWeight: 700, cursor: post.author?.id ? 'pointer' : 'default' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToProfile(post.author?.id);
                                    }}
                                  >
                                    {(post.author.displayName || 'U').charAt(0).toUpperCase()}
                                  </Avatar>
                                ) : (
                                  <Box sx={{ width: 34 }} />
                                )}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  {showIdentity && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.35 }}>
                                      <Typography
                                        variant="subtitle2"
                                        component="div"
                                        sx={{ cursor: post.author?.id ? 'pointer' : 'default' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          goToProfile(post.author?.id);
                                        }}
                                      >
                                        {post.author.displayName || 'Unknown Student'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" component="div">
                                        {formatTimeAgo(post.createdAt)} ago
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.6, maxWidth: { xs: '96%', md: '88%' } }}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        px: 1.25,
                                        py: 0.85,
                                        borderRadius: 2.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : theme.palette.background.paper),
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} component="div">
                                        {post.content}
                                      </Typography>
                                    </Paper>
                                    {!showIdentity && formatChatTime(post.createdAt) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '2.2rem', textAlign: 'center', fontWeight: 600 }} component="div">
                                        {formatChatTime(post.createdAt)}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      sx={{ minWidth: 0, px: 0 }}
                                      onClick={() => setReplyTarget({
                                        postId: post.id,
                                        label: `Replying to ${post.author.displayName || 'Student'}`,
                                      })}
                                    >
                                      Reply
                                    </Button>
                                    {me?.id && (post.author?.id === me.id || isSuperAdmin) && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        sx={{ minWidth: 0, px: 0 }}
                                        disabled={deletingPostId === post.id}
                                        onClick={() => void handleDeletePost(post.id)}
                                      >
                                        {deletingPostId === post.id ? 'Deleting...' : 'Delete'}
                                      </Button>
                                    )}
                                  </Box>

                                  {(post.comments || []).map((comment) => (
                                    <Box key={comment.id} sx={{ mt: 1.25, pl: 1.25, borderLeft: '2px solid', borderColor: 'divider' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          component="div"
                                          sx={{ cursor: comment.author?.id ? 'pointer' : 'default' }}
                                          onClick={() => goToProfile(comment.author?.id)}
                                        >
                                          {comment.author?.displayName || 'Student'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" component="div">
                                          {formatTimeAgo(comment.createdAt)} ago
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" sx={{ mt: 0.25 }} component="div">{comment.content || '[deleted]'}</Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        sx={{ minWidth: 0, px: 0, mt: 0.25 }}
                                        onClick={() => setReplyTarget({
                                          postId: post.id,
                                          parentId: comment.id,
                                          label: `Replying to ${comment.author?.displayName || 'Student'}`,
                                        })}
                                      >
                                        Reply
                                      </Button>
                                      {me?.id && (comment.author?.id === me.id || isSuperAdmin) && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          color="error"
                                          sx={{ minWidth: 0, px: 0, mt: 0.25, ml: 1 }}
                                          disabled={deletingCommentId === comment.id}
                                          onClick={() => void handleDeleteComment(post.id, comment.id)}
                                        >
                                          {deletingCommentId === comment.id ? 'Removing...' : 'Remove'}
                                        </Button>
                                      )}

                                      {(comment.children || []).map((child) => (
                                        <Box key={child.id} sx={{ mt: 1, pl: 1.25, borderLeft: '2px dashed', borderColor: 'divider' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                            <Typography
                                              variant="caption"
                                              fontWeight={700}
                                              component="div"
                                              sx={{ cursor: child.author?.id ? 'pointer' : 'default' }}
                                              onClick={() => goToProfile(child.author?.id)}
                                            >
                                              {child.author?.displayName || 'Student'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" component="div">
                                              {formatTimeAgo(child.createdAt)} ago
                                            </Typography>
                                          </Box>
                                          <Typography variant="body2" sx={{ mt: 0.25 }} component="div">{child.content || '[deleted]'}</Typography>
                                          {me?.id && (child.author?.id === me.id || isSuperAdmin) && (
                                            <Button
                                              size="small"
                                              variant="text"
                                              color="error"
                                              sx={{ minWidth: 0, px: 0, mt: 0.25 }}
                                              disabled={deletingCommentId === child.id}
                                              onClick={() => void handleDeleteComment(post.id, child.id)}
                                            >
                                              {deletingCommentId === child.id ? 'Removing...' : 'Remove'}
                                            </Button>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                            {idx < orderedChatPosts.length - 1 && !isSameDay(post.createdAt, orderedChatPosts[idx + 1].createdAt) && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 1.1,
                                    py: 0.2,
                                    borderRadius: 99,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.palette.background.paper),
                                    color: 'text.secondary',
                                    fontWeight: 700,
                                  }}
                                >
                                  {formatDaySeparator(orderedChatPosts[idx + 1].createdAt)}
                                </Typography>
                              </Box>
                            )}
                            </React.Fragment>
                            );
                          })}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        disabled={!chatCursor || chatLoading}
                        onClick={() => selectedClub?.id && void loadClubChat(selectedClub.id, chatCursor || undefined)}
                      >
                        {chatLoading && chatPosts.length > 0 ? 'Loading...' : 'Load older messages'}
                      </Button>
                    </Box>

                    {replyTarget && (
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" component="div">{replyTarget.label}</Typography>
                        <Button size="small" variant="text" onClick={() => setReplyTarget(null)}>Cancel</Button>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <TextField
                        fullWidth
                        multiline
                        inputRef={composerInputRef}
                        minRows={2}
                        maxRows={4}
                        placeholder={replyTarget ? 'Write a reply...' : 'Write a message to the club chat...'}
                        value={newMessage}
                        onChange={(event) => setNewMessage(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void handleSendMessage();
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        disabled={!newMessage.trim() || sendingMessage}
                        onClick={() => {
                          void handleSendMessage();
                        }}
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
          {selectedClub?.profileUrl && (
            <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => selectedClub && handleOpenProfile(selectedClub)}>
              Visit Official Page
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {!selectedClub?.joined && (
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={actionLoading === selectedClub?.id}
              onClick={() => {
                if (selectedClub) void handleJoinClub(selectedClub.id);
              }}
            >
              {isJoinLoadingFor(selectedClub?.id) ? 'Joining...' : isLeaveLoadingFor(selectedClub?.id) ? 'Leaving...' : 'Join Club'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={mobileInfoOpen} onClose={() => setMobileInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Club Info</DialogTitle>
        <DialogContent dividers>
          {selectedClub && (
            <>
              <Typography variant="subtitle2" color="text.secondary" component="div">About</Typography>
              {selectedClub.description ? (
                <Box
                  sx={{ mt: 1, '& p': { m: 0, mb: 1.25 }, '& a': { textDecoration: 'underline' } }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedClub.description || '') }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedClub.shortDesc || 'No description available yet.'}
                </Typography>
              )}

              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" component="div">Members</Typography>
                <Typography variant="body2" component="div">
                  {(selectedClub.memberCount ?? 0).toLocaleString()} member{(selectedClub.memberCount ?? 0) === 1 ? '' : 's'}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    disabled={!selectedClub.joined}
                    onClick={() => {
                      setMobileInfoOpen(false);
                      void handleOpenMembers();
                    }}
                  >
                    View Members
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedClub?.name ? `${selectedClub.name} members` : 'Members'}
        </DialogTitle>
        <DialogContent dividers>
          {membersLoading && members.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members found.
            </Typography>
          ) : (
            <List dense disablePadding>
              {members.map((member) => (
                <ListItemButton
                  key={`${member.id}-${member.joinedAt}`}
                  sx={{ px: 0.5, borderRadius: 1 }}
                  onClick={() => goToProfile(member.id)}
                >
                  <ListItemAvatar>
                    <Avatar src={member.avatarUrl || undefined} sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}>
                      {(member.displayName || member.firstName || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.displayName || [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Unknown Student'}
                    secondary={member.major || undefined}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="text"
            disabled={!membersCursor || membersLoading || !selectedClub?.id}
            onClick={() => selectedClub?.id && void loadClubMembers(selectedClub.id, membersCursor || undefined)}
          >
            {membersLoading && members.length > 0 ? 'Loading...' : 'Load more'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setMembersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clubs;